import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, Search, Plus, CalendarClock, Trash2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { NET_COLLECTIONS, NET_STATUSES } from './collections';
import NetworkContactSheet from './NetworkContactSheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_ORDER = ['prospect', 'contact', 'partenaire', 'ambassadeur'];

function KanbanCard({ c, onOpen }) {
    const m = NET_COLLECTIONS[c.collection];
    return (
        <div
            draggable
            onDragStart={e => e.dataTransfer.setData('text/contact-id', c.id)}
            onClick={() => onOpen(c.id)}
            data-testid="crm-card"
            className="rounded-lg border border-border bg-surface p-3 cursor-grab hover:shadow-soft hover:border-primary/40 transition-all active:cursor-grabbing"
        >
            <div className="font-medium text-sm leading-snug">{c.full_name}</div>
            <div className="text-[11px] text-foreground/50 truncate">{[c.role_function, c.organization].filter(Boolean).join(' · ')}</div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {m && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${m.color}`}><m.icon size={9} /> {m.label}</span>}
                {c.score_total != null && <span className="px-1.5 py-0.5 rounded-full bg-primary-light text-primary text-[10px] font-semibold">{Math.round(c.score_total)}</span>}
                {c.country && <span className="text-[10px] text-foreground/40">{c.country}</span>}
            </div>
        </div>
    );
}

function TasksPanel({ api }) {
    const [tasks, setTasks] = useState([]);
    const [title, setTitle] = useState('');
    const [dueAt, setDueAt] = useState('');

    const load = useCallback(async () => {
        try { setTasks((await api.get('/network/tasks')).data.items || []); } catch { /* silencieux */ }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!title.trim()) return;
        try {
            await api.post('/network/tasks', { title: title.trim(), due_at: dueAt || null });
            setTitle(''); setDueAt('');
            toast.success('Rappel créé');
            load();
        } catch { toast.error('Création échouée'); }
    };

    const toggle = async (t) => {
        try { await api.put(`/network/tasks/${t.id}`, { done: !t.done }); load(); } catch { toast.error('Échec'); }
    };
    const remove = async (t) => {
        try { await api.delete(`/network/tasks/${t.id}`); load(); } catch { toast.error('Échec'); }
    };

    const overdue = (t) => t.due_at && !t.done && new Date(t.due_at) < new Date();

    return (
        <div className="rounded-xl border border-border bg-surface p-5 space-y-4" data-testid="crm-tasks-panel">
            <div className="flex items-center gap-2">
                <CalendarClock size={16} className="text-primary" />
                <h3 className="font-heading text-lg">Rappels & tâches</h3>
                <span className="text-xs text-foreground/50">{tasks.filter(t => !t.done).length} en cours</span>
            </div>
            <div className="flex flex-wrap gap-2">
                <Input className="flex-1 min-w-[200px]" placeholder="Nouveau rappel… (ex: Relancer M. Diop avant le sommet)"
                       value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()}
                       data-testid="crm-task-title" />
                <Input type="datetime-local" className="w-52" value={dueAt} onChange={e => setDueAt(e.target.value)} data-testid="crm-task-due" />
                <Button onClick={add} data-testid="crm-task-add"><Plus size={14} className="mr-1" /> Ajouter</Button>
            </div>
            {tasks.length === 0 ? (
                <div className="text-sm text-foreground/50 py-2">Aucune tâche — créez votre premier rappel.</div>
            ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {tasks.map(t => (
                        <div key={t.id} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${t.done ? 'opacity-50 border-border' : overdue(t) ? 'border-red-300 bg-red-50/50' : 'border-border'}`} data-testid="crm-task-row">
                            <button type="button" onClick={() => toggle(t)} data-testid="crm-task-toggle"
                                    className={`h-5 w-5 rounded border flex items-center justify-center shrink-0 ${t.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-border hover:border-primary'}`}>
                                {t.done && <Check size={12} />}
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm truncate ${t.done ? 'line-through' : ''}`}>{t.title}</div>
                                <div className="text-[11px] text-foreground/50">
                                    {t.network_contacts?.full_name && <span className="font-medium">{t.network_contacts.full_name} · </span>}
                                    {t.due_at ? new Date(t.due_at).toLocaleString('fr-FR') : 'Sans échéance'}
                                    {overdue(t) && <span className="text-red-600 font-medium"> · En retard</span>}
                                </div>
                            </div>
                            <button type="button" onClick={() => remove(t)} className="text-foreground/30 hover:text-red-500 transition-colors" data-testid="crm-task-delete">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function NetworkCrm({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [collection, setCollection] = useState('');
    const [sheetId, setSheetId] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const searchTimer = useRef(null);

    const load = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const r = await api.get('/network/pipeline', { params });
            setPipeline(r.data);
        } catch { toast.error('Chargement du pipeline impossible'); }
        finally { setLoading(false); }
    }, [api]);

    useEffect(() => { load(); }, [load]);

    const refresh = useCallback(() => {
        const params = {};
        if (q) params.q = q;
        if (collection) params.collection = collection;
        load(params);
    }, [q, collection, load]);

    const onSearch = (v) => {
        setQ(v);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            const params = {};
            if (v) params.q = v;
            if (collection) params.collection = collection;
            load(params);
        }, 350);
    };

    const onCollection = (v) => {
        setCollection(v);
        const params = {};
        if (q) params.q = q;
        if (v) params.collection = v;
        load(params);
    };

    const moveTo = async (contactId, status) => {
        try {
            await api.put(`/network/contacts/${contactId}`, { status_relation: status });
            toast.success(`Statut → ${NET_STATUSES[status].label}`);
            refresh();
        } catch { toast.error('Déplacement échoué'); }
    };

    const onDrop = (e, status) => {
        e.preventDefault();
        setDragOver(null);
        const id = e.dataTransfer.getData('text/contact-id');
        if (id) moveTo(id, status);
    };

    return (
        <div className="space-y-6" data-testid="network-crm">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
                    <Input className="pl-9" placeholder="Rechercher dans le pipeline…" value={q}
                           onChange={e => onSearch(e.target.value)} data-testid="crm-search" />
                </div>
                <select className="h-9 rounded-md border border-input bg-background px-2.5 text-sm"
                        value={collection} onChange={e => onCollection(e.target.value)} data-testid="crm-filter-collection">
                    <option value="">Toutes collections</option>
                    {Object.entries(NET_COLLECTIONS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                </select>
            </div>

            {loading || !pipeline ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" /></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {STATUS_ORDER.map(st => {
                        const col = pipeline[st] || { total: 0, items: [] };
                        const meta = NET_STATUSES[st];
                        return (
                            <div
                                key={st}
                                onDragOver={e => { e.preventDefault(); setDragOver(st); }}
                                onDragLeave={() => setDragOver(d => d === st ? null : d)}
                                onDrop={e => onDrop(e, st)}
                                data-testid={`crm-column-${st}`}
                                className={`rounded-xl border p-3 space-y-2 min-h-[300px] transition-colors ${dragOver === st ? 'border-primary bg-primary-light/30' : 'border-border bg-surface-muted/40'}`}
                            >
                                <div className="flex items-center justify-between px-1 pb-1">
                                    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
                                    <span className="text-xs text-foreground/50 font-medium" data-testid={`crm-count-${st}`}>{col.total}</span>
                                </div>
                                {col.items.map(c => <KanbanCard key={c.id} c={c} onOpen={setSheetId} />)}
                                {col.total > col.items.length && (
                                    <div className="text-center text-[11px] text-foreground/40 pt-1">
                                        + {col.total - col.items.length} autres (affinez via recherche)
                                    </div>
                                )}
                                {col.items.length === 0 && (
                                    <div className="text-center text-xs text-foreground/40 py-6">Glissez un contact ici</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <TasksPanel api={api} />

            {sheetId && (
                <NetworkContactSheet
                    contactId={sheetId}
                    onClose={() => setSheetId(null)}
                    onEdit={() => {}}
                    onDeleted={refresh}
                    api={api}
                />
            )}
        </div>
    );
}
